/**
 * Since /b2b/authenticate goes through the proxy, B2B players have a proxy token like the skin players
 * (services/b2bProxySession): they call the normal routes, with the proxy token as Bearer, and the
 * player comes from the passport of that token.
 *
 * Fallback: VUE_APP_B2B_TWIN_ROUTES=true (build time) sends B2B players to the /b2b/ twins of the proxy
 * routes (rocky-bets and rocky-definition) as before 2026-11-02: the player is the one of the `b2bToken`
 * in the body (the B2B token of `user/getB2bToken`, saved server side by /b2b/authenticate). The twins
 * are still supported, with their deprecation planned. Non-B2B skins (`settings/enableB2b`) always use
 * the normal routes, body and proxy token.
 */
const B2B_PATHS = [
    ['/cart/v3/', '/cart/b2b/'],
    ['/bet/v3/', '/bet/b2b/'],
    ['/cart/v2/self/review/accept-bet', '/cart/b2b/self/review/accept-bet'],
    ['/rocky-definition/definition/bet/review/self/reject', '/rocky-definition/definition/bet/b2b/review/self/reject'],
];

/** True when a B2B player goes to the /b2b/ twins: the fallback (VUE_APP_B2B_TWIN_ROUTES=true) only. */
export function useB2bTwinRoutes(rootGetters) {
    return rootGetters['settings/getB2bEnabled'] === true && process.env.VUE_APP_B2B_TWIN_ROUTES === 'true';
}

/** The path of the normal route, or its B2B twin with the fallback on. */
export function proxyPath(rootGetters, path) {
    if (!useB2bTwinRoutes(rootGetters)) return path;
    const twin = B2B_PATHS.find(([normal]) => path.startsWith(normal));
    return twin ? twin[1] + path.slice(twin[0].length) : path;
}

/** The body of the normal route, plus the b2bToken with the fallback on. */
export function proxyBody(rootGetters, body) {
    if (!useB2bTwinRoutes(rootGetters)) return body;
    return {...body, b2bToken: rootGetters['user/getB2bToken']};
}

/**
 * The proxy token as Bearer. A B2B player without a proxy token gets no header: Fusio checks any
 * Authorization header it gets, even on a public route, and an empty `Bearer` got a 401 "No
 * authorization token was provided" before reaching rocky-bets. A valid token passes on the normal
 * routes and on the twins, both public and private.
 */
export function proxyHeaders(rootGetters, token) {
    if (rootGetters['settings/getB2bEnabled'] === true && !token) return {};
    return {Authorization: `Bearer ${token}`};
}
